'use client';

import SvgChevronleft from '@common/icon/SvgChevronleft';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Image } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';

export interface MarketReviewItem {
  rating: number;
  /** 작성자 닉네임. 결측이면 null — 빈 문자열로 뭉개지 않는다 */
  nickname: string | null;
  date: string;
  content: string;
  imageUrlList?: string[];
  productName?: string;
  answer?: string;
  answerDate?: string;
}

interface MarketReviewDetailContentProps {
  reviews: MarketReviewItem[];
  initialIndex?: number;
}

export const MarketReviewDetailContent = ({
  reviews,
  initialIndex = 0,
}: MarketReviewDetailContentProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonPositions, setButtonPositions] = useState({
    left: { top: 0, left: 0 },
    right: { top: 0, right: 0 },
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start' });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedImageIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- embla 캐러셀 외부 시스템 구독 초기 동기화
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(0, true);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- 리뷰 전환 시 embla 외부 시스템과 이미지 인덱스 상태 동기화
      setSelectedImageIndex(0);
    }
  }, [currentIndex, emblaApi]);

  const updateButtonPositions = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;
    setButtonPositions({
      left: { top: centerY, left: rect.left - 120 },
      right: { top: centerY, right: window.innerWidth - rect.right - 120 },
    });
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    updateButtonPositions();
    const resizeObserver = new ResizeObserver(() => updateButtonPositions());
    resizeObserver.observe(container);
    window.addEventListener('resize', updateButtonPositions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateButtonPositions);
    };
  }, [currentIndex, updateButtonPositions]);

  const review = reviews[currentIndex];
  if (!review) return null;

  const imageUrlList = review.imageUrlList ?? [];
  const isPhotoReview = imageUrlList.length > 0;
  const isPrevDisabled = currentIndex === 0;
  const isNextDisabled = currentIndex === reviews.length - 1;

  return (
    <>
      <div ref={containerRef} className="grow">
        <div
          className={cn(
            'relative flex size-full gap-20',
            'mobile-tablet:flex-col',
          )}
        >
          {isPhotoReview && (
            <div
              className={cn(
                'min-w-0 shrink-0 basis-[544px]',
                'mobile-tablet:width-full mobile-tablet:basis-0',
              )}
            >
              <div className="relative aspect-square w-full">
                <div className="size-full overflow-hidden" ref={emblaRef}>
                  <div className="flex h-full">
                    {imageUrlList.map((url, index) => (
                      <div
                        key={index}
                        className="relative aspect-square min-w-0 flex-[0_0_100%]"
                      >
                        <Image
                          fill
                          src={url}
                          alt="리뷰 이미지"
                          className="object-cover object-center"
                          sizes="850px"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {imageUrlList.length > 1 && (
                  <div className="absolute bottom-0 right-0 mb-16 ml-auto mr-16 w-fit rounded-pill bg-Gray-black/20 px-8 py-2 font-600 text-Gray-white typo-caption2">
                    {selectedImageIndex + 1} / {imageUrlList.length}
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className={cn(
              'flex min-w-0 flex-col gap-20',
              'mobile-tablet:px-20 mobile-tablet:pb-20',
            )}
          >
            <div className="flex flex-col">
              <Rating value={review.rating} size={16} className="mb-8" />
              <div className="flex items-center gap-4 font-400 text-Gray-550 typo-caption1">
                {review.nickname}
                {review.nickname && review.date ? (
                  <div className="mb-6 h-10 w-1 bg-Gray-300" />
                ) : null}
                {review.date}
              </div>
              {review.productName && (
                <p className="mb-16 break-all text-start font-400 text-Gray-600 typo-body4">
                  {review.productName}
                </p>
              )}
              <p className="whitespace-pre-line break-all text-start font-500 text-Gray-800 typo-body3">
                {review.content}
              </p>
            </div>
            {review.answer && (
              <div className="flex flex-col rounded-medium bg-Gray-25 p-20">
                <div className="flex items-center justify-between pb-4">
                  <p className="font-600 text-Gray-700 typo-body4">판매자</p>
                  {review.answerDate && (
                    <p className="font-400 text-Gray-550 typo-caption1">
                      {review.answerDate}
                    </p>
                  )}
                </div>
                <p className="whitespace-pre-line break-all font-400 text-Gray-700 typo-body4">
                  {review.answer}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {reviews.length > 1 &&
        typeof window !== 'undefined' &&
        createPortal(
          <>
            <button
              className={cn(
                'fixed z-50 size-52 -translate-y-1/2 rounded-pill border-1 border-Gray-300 bg-Gray-white flex-center',
                'mobile-tablet:hidden',
                isPrevDisabled && 'cursor-not-allowed opacity-50',
              )}
              style={{
                top: buttonPositions.left.top,
                left: buttonPositions.left.left,
              }}
              onClick={
                isPrevDisabled ? undefined : () => setCurrentIndex((i) => i - 1)
              }
            >
              <SvgChevronleft className="size-36 text-Gray-600" />
            </button>
            <button
              className={cn(
                'fixed z-50 size-52 -translate-y-1/2 rounded-pill border-1 border-Gray-300 bg-Gray-white flex-center',
                'mobile-tablet:hidden',
                isNextDisabled && 'cursor-not-allowed opacity-50',
              )}
              style={{
                top: buttonPositions.right.top,
                right: buttonPositions.right.right,
              }}
              onClick={
                isNextDisabled ? undefined : () => setCurrentIndex((i) => i + 1)
              }
            >
              <SvgChevronright className="size-36 text-Gray-600" />
            </button>
          </>,
          document.body,
        )}
    </>
  );
};
